import Link from 'next/link';

import { cn } from '@kit/ui/utils';

import { platformBrand } from '~/config/platform-brand.config';

/**
 * The platform's wordmark — whose product this is.
 *
 * ## Which brand, and why it is a parameter now
 *
 * `NEXT_PUBLIC_PLATFORM_BRAND` selects it; `platform-brand.config.ts` holds the
 * set and says why it is an id rather than a path. The mark was a hardcoded
 * Unicity Labs asset, which is right for one customer and wrong for a bid whose
 * whole point is that the platform is e&'s.
 *
 * ## Rendered as a CSS MASK, not an `<img>`
 *
 * Two reasons, and both are load-bearing rather than preference. An SVG loaded
 * through `<img>` is an isolated document and cannot inherit `currentColor`, so
 * it would need a light and a dark copy of every asset; Next's image optimizer
 * also rejects SVG by default. Masking the shape and filling with
 * `currentColor` means one file serves the marketing site's near-black canvas
 * and the signed-in app's light theme. Inlining would work too and ships 26KB
 * and 102 paths into every page's markup.
 *
 * ## The typographic fallback is not a placeholder for the sake of one
 *
 * A brand with no asset renders its NAME in type. That is the honest state for
 * e& until the official file lands, and the alternative — approximating a
 * customer's registered wordmark by hand — puts a counterfeit trademark into a
 * bid and, worse, looks finished enough that nobody replaces it.
 *
 * The accessible name comes from the brand either way, so a screen reader and
 * the screen always say the same thing. That is the failure this component
 * could otherwise hide: a build serving one company's mark while announcing
 * another's.
 */
/**
 * Is this a long wordmark rather than a compact mark?
 *
 * Three-to-one is the line, and it is not fussy: Unicity's lockup is 5:1 and
 * e&'s mark is 1.07:1, so anything plausible falls clearly on one side. What
 * matters is that the question is asked of the ASSET's shape and not of the
 * brand's name — a rule keyed on an id has to be edited every time a brand is
 * added, and the edit that gets forgotten produces a logo four times too tall.
 */
function isLockup(ratio: string) {
  const [width, height] = ratio.split('/').map((part) => Number(part.trim()));

  return (width ?? 0) / (height || 1) >= 3;
}

export function LogoImage({ className }: { className?: string }) {
  const brand = platformBrand;

  if (!brand.wordmark) {
    return (
      <span
        aria-label={brand.label}
        className={cn(
          'block text-xl leading-none font-semibold tracking-tight',
          className,
        )}
        data-brand={brand.id}
        data-test={'app-logo'}
        role={'img'}
      >
        {brand.text}
      </span>
    );
  }

  const source = `url(${brand.wordmark})`;

  /*
   * A coloured mark is PAINTED; a monochrome one is masked.
   *
   * e&'s asset is a single path filled #F20519, and that red is the brand.
   * Masking it would fill the shape with whatever colour the header inherits —
   * the right silhouette in the wrong company's colour, on a bid document. So
   * a `tint: 'own'` brand is drawn as a background image, which keeps the file
   * exactly as its owner drew it.
   *
   * `background-image` rather than `<img>` or `next/image`: the optimizer
   * rejects SVG by default, and this keeps one element, one ratio and one set
   * of sizing rules across both kinds of mark.
   */
  const paint =
    brand.tint === 'own'
      ? {
          backgroundImage: source,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }
      : {
          backgroundColor: 'currentColor',
          maskImage: source,
          WebkitMaskImage: source,
          maskSize: 'contain',
          WebkitMaskSize: 'contain',
          maskRepeat: 'no-repeat',
          WebkitMaskRepeat: 'no-repeat',
          maskPosition: 'center',
          WebkitMaskPosition: 'center',
        };

  return (
    <span
      aria-label={brand.label}
      /*
       * A long lockup is sized by WIDTH; a compact mark by HEIGHT.
       *
       * `w-[120px]` suits Unicity's 5:1 wordmark and would make e&'s near-square
       * mark 120px TALL — four times the height of the row it sits in. Read off
       * the ratio rather than the brand id, so the rule is about the shape of
       * the asset and a third brand needs no new branch.
       */
      className={cn(
        isLockup(brand.ratio)
          ? 'block w-[120px] lg:w-[150px]'
          : 'block h-10 lg:h-12',
        className,
      )}
      data-brand={brand.id}
      data-test={'app-logo'}
      role={'img'}
      style={{ aspectRatio: brand.ratio, ...paint }}
    />
  );
}

export function AppLogo({
  href,
  label,
  className,
}: {
  href?: string | null;
  className?: string;
  label?: string;
}) {
  if (href === null) {
    return <LogoImage className={className} />;
  }

  return (
    <Link
      aria-label={label ?? 'Home Page'}
      href={href ?? '/'}
      prefetch={true}
      className="mx-auto md:mx-0"
    >
      <LogoImage className={className} />
    </Link>
  );
}
